import { __awaiter } from "tslib";
import axios from 'axios';
import { merge } from '@fluentui/react';
import { useEffect, useState } from 'react';
import { useQueries, useQuery, } from '@tanstack/react-query';
import { formatOdataQuery } from './oDataQueryString';
import { LIST_DATA_STALE_TIME_MSEC } from '../../constants';
import { useAnalytics } from '@msrceng/analytics';
import { useOApiReady } from './util/useOApiReady';
const defaultOptions = {
    enabled: true,
    pageLength: 20,
    resetPageOnQueryChange: false,
    useQueryOptions: {
        keepPreviousData: true,
        cacheTime: LIST_DATA_STALE_TIME_MSEC,
        staleTime: LIST_DATA_STALE_TIME_MSEC,
    },
};
/** Premade Odata API for querying odata entities with cached & preloaded paging. */
export const useODataReadListApi = (
/** The url path (relative) of this entity on top of the apiRoot */
endpointName, 
/** Query filters & params for the paged GET query included in this definiton */
query = {}, 
/** Additional options */
options) => {
    var _a, _b;
    const { appInsights } = useAnalytics();
    const { enabled: isEnabledInOptions, isAuthRequired, isVerificationRequired, pageLength, resetPageOnQueryChange, uniqueIdentifier, useQueryOptions, } = merge({}, defaultOptions, options);
    /** Did our gates for verification/auth/etc pass? */
    const isReady = useOApiReady({ isAuthRequired, isVerificationRequired });
    /** Did *all* pre-request checks pass? */
    const enabled = isEnabledInOptions && isReady;
    const [rowCount, setRowCount] = useState();
    const oDataQuery = merge({}, defaultQuery, {
        top: pageLength,
    }, query);
    const [page, setPage] = useState(0);
    useEffect(() => {
        if (resetPageOnQueryChange) {
            setPage(0);
        }
    }, [query, resetPageOnQueryChange]);
    // let's fetch the current page and also pre-fetch the next 2 pages
    // so our user feels like this is loading fast.
    const queryOpts = [
        makePageQueryOptions({
            endpointName,
            page,
            oDataQuery,
            uniqueIdentifier,
            enabled,
            useQueryOptions,
            appInsights,
            setRowCount,
        }),
        makePageQueryOptions({
            endpointName,
            page: page + 1,
            oDataQuery,
            uniqueIdentifier,
            enabled,
            useQueryOptions,
            appInsights,
        }),
    ];
    //this type def is a total hack that this api really should just support
    const api = useQueries({
        queries: queryOpts,
    });
    const nextPage = api[1];
    //prefetch the 3rd page when page 2 is finished
    const pageAfterNextApi = useQuery(makePageQueryOptions({
        endpointName,
        page: page + 2,
        oDataQuery,
        uniqueIdentifier,
        enabled: enabled &&
            !nextPage.isLoading &&
            !!nextPage.data &&
            nextPage.data.length > 1,
        useQueryOptions,
        appInsights,
    }));
    const isLoadingNext = (_a = api[1]) === null || _a === void 0 ? void 0 : _a.isFetching;
    const hasNext = isLoadingNext || (((_b = api[1]) === null || _b === void 0 ? void 0 : _b.data) && api[1].data.length > 0);
    const hasPageAfterNext = (pageAfterNextApi === null || pageAfterNextApi === void 0 ? void 0 : pageAfterNextApi.data) && pageAfterNextApi.data.length > 0;
    const hasPrevious = page > 0;
    return Object.assign(Object.assign({}, api[0]), { page,
        hasNext,
        hasPageAfterNext,
        hasPrevious, previous: () => setPage(Math.max(page - 1, 0)), next: () => setPage(Math.min(page + 1)), goToPage: (newPage) => setPage(newPage), isLoadingNext,
        rowCount });
};
//prefetch stuff
const makePageQueryOptions = ({ enabled, endpointName, page, oDataQuery, uniqueIdentifier, useQueryOptions, appInsights, setRowCount, }) => {
    const skip = page * ((oDataQuery === null || oDataQuery === void 0 ? void 0 : oDataQuery.top) || 10);
    const query = Object.assign(Object.assign({}, oDataQuery), { skip });
    return Object.assign({ queryKey: [endpointName, query], queryFn: () => getEntity({
            oDataQuery: query,
            endpointName,
            uniqueIdentifier,
            setRowCount,
        }), onError: error => appInsights === null || appInsights === void 0 ? void 0 : appInsights.trackException({
            exception: error,
        }), enabled }, useQueryOptions);
};
const getEntity = ({ oDataQuery, endpointName, uniqueIdentifier, setRowCount, }) => __awaiter(void 0, void 0, void 0, function* () {
    if (!axios.defaults.baseURL) {
        console.error('NOT YET READY');
        throw new Error('not ready');
    }
    const oDataString = formatOdataQuery(oDataQuery, uniqueIdentifier, false);
    return axios
        .get(`${endpointName}${oDataString && oDataString}`)
        .then(res => {
        if (setRowCount) {
            setRowCount(res.data['@odata.count']);
        }
        return res.data.value;
    });
});
const defaultQuery = {
// orderBy: { ascending: false, key: 'modifiedOn' },
};
